import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Tv, Smartphone, Crown, Zap, Brain, Lock, Users, Loader2, Plus, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { partyApi, saveHost, savePlayer } from "@/lib/party";

const MODES = [
  { id: "trivia", label: "Trivia Brawl", icon: Brain, color: "#00F0FF",
    desc: "Fast-paced trivia — fastest correct answers score most. Clean & competitive.", tier: "guest" },
  { id: "punchline", label: "Punchline Party", icon: Zap, color: "#FF003C",
    desc: "Write the funniest answer to wild prompts, then everyone votes. Chaos & laughs.", tier: "free" },
];

export default function Party() {
  const { user, setShowAuth } = useAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState("");
  const [joinCode, setJoinCode] = useState((new URLSearchParams(window.location.search).get("code") || "").toUpperCase());
  const [joinName, setJoinName] = useState("");
  const [joining, setJoining] = useState(false);
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState("default");
  const [packModal, setPackModal] = useState(false);
  const [packName, setPackName] = useState("");
  const [packText, setPackText] = useState("");
  const [savingPack, setSavingPack] = useState(false);

  const loadPacks = () => partyApi.listPacks().then((d) => setPacks([...d.packs, ...d.custom])).catch(() => {});
  useEffect(() => { loadPacks(); }, [user]); // eslint-disable-line

  const tier = user?.is_premium ? "premium" : user ? "free" : "guest";
  const can = (m) => m.tier === "guest" || (m.tier === "free" && (tier === "free" || tier === "premium"));

  const host = async (mode) => {
    setCreating(mode);
    try {
      const r = await partyApi.create(mode, mode === "punchline" ? selectedPack : "default");
      saveHost(r.code, r.host_token);
      navigate(`/party/host/${r.code}`);
    } catch {
      toast.error("Couldn't create room. Try again.");
    } finally {
      setCreating("");
    }
  };

  const savePack = async () => {
    const prompts = packText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!packName.trim() || prompts.length < 3) { toast.error("Add a name and at least 3 prompts."); return; }
    setSavingPack(true);
    try {
      const np = await partyApi.createPack(packName.trim(), prompts);
      toast.success("Custom pack saved! 🎉");
      setPackModal(false); setPackName(""); setPackText("");
      await loadPacks();
      setSelectedPack(np.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save pack.");
    } finally {
      setSavingPack(false);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4 || !joinName.trim()) return;
    setJoining(true);
    try {
      const p = await partyApi.join(code, joinName.trim());
      savePlayer(code, p);
      navigate(`/party/play/${code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't join. Check the code.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="party-page">
      <Seo title="Mania Live — Streamable Multiplayer Party Games" description="Host a live party game on your screen and let friends join from their phones with a room code. Trivia Brawl & Punchline Party — perfect for streams and game nights." />

      <div className="text-center mb-12">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-neon-pink/15 border border-neon-pink/50 text-neon-pink text-xs font-bold uppercase tracking-widest mb-4">
          <Tv className="w-3.5 h-3.5" /> Stream-Ready Multiplayer
        </span>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">Mania <span className="text-neon-cyan">Live</span></h1>
        <p className="text-zinc-400 mt-4 max-w-xl mx-auto">Host on the big screen. Friends join from their phones with a 4-letter code. Built for game nights and streams.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 items-start">
        {/* HOST */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <Tv className="w-6 h-6 text-neon-cyan" />
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">Host a Game</h2>
          </div>
          <div className="space-y-4">
            {MODES.map((m) => {
              const Icon = m.icon;
              const locked = !can(m);
              return (
                <motion.div key={m.id} whileHover={{ y: -3 }}
                  className={`relative rounded-2xl border-2 p-6 ${locked ? "border-zinc-800 opacity-70" : "border-zinc-700 hover:border-neon-cyan"} bg-zinc-950 transition-colors`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${m.color}22`, border: `1px solid ${m.color}55` }}>
                      <Icon className="w-6 h-6" style={{ color: m.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-bold text-lg uppercase tracking-tight">{m.label}</h3>
                      <p className="text-sm text-zinc-400 mt-1">{m.desc}</p>
                      {m.id === "punchline" && !locked && (
                        <div className="mt-4" data-testid="pack-picker">
                          <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Prompt pack</p>
                          <div className="flex flex-wrap gap-2">
                            {packs.map((p) => (
                              <button key={p.id} disabled={p.locked}
                                onClick={() => p.locked ? navigate("/subscribe") : setSelectedPack(p.id)}
                                data-testid={`pack-${p.id.replace(":", "-")}`}
                                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest rounded-full border-2 transition-colors flex items-center gap-1 ${
                                  selectedPack === p.id ? "bg-neon-pink text-white border-neon-pink"
                                  : p.locked ? "border-zinc-800 text-zinc-600" : "border-zinc-700 text-zinc-300 hover:border-white"}`}>
                                {p.locked && <Crown className="w-3 h-3 text-neon-yellow" />}{p.label}
                              </button>
                            ))}
                            {tier === "premium" && (
                              <button onClick={() => setPackModal(true)} data-testid="create-pack-btn"
                                className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest rounded-full border-2 border-dashed border-neon-lime text-neon-lime hover:bg-neon-lime hover:text-black flex items-center gap-1">
                                <Plus className="w-3 h-3" /> New
                              </button>
                            )}
                          </div>
                          {tier !== "premium" && <p className="text-[11px] text-zinc-600 mt-2">👑 packs & custom packs are Premium.</p>}
                        </div>
                      )}
                      {locked ? (
                        <button onClick={() => setShowAuth(true)} data-testid={`host-locked-${m.id}`}
                          className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-neon-yellow hover:text-white">
                          <Lock className="w-3.5 h-3.5" /> Log in to unlock
                        </button>
                      ) : (
                        <Button onClick={() => host(m.id)} disabled={creating === m.id} data-testid={`host-${m.id}`}
                          className="mt-4 bg-neon-cyan text-black rounded-none font-bold uppercase text-xs tracking-widest h-10 px-6 hover:bg-white">
                          {creating === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Host this"}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs text-zinc-500 font-mono">
            <Users className="w-3.5 h-3.5" />
            {tier === "guest" && <span>Guest: up to 4 players, Trivia only.</span>}
            {tier === "free" && <span>Member: up to 8 players, both modes.</span>}
            {tier === "premium" && <span className="text-neon-yellow flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> Premium: up to 12 players, private rooms.</span>}
          </div>
          {tier !== "premium" && (
            <p className="mt-2 text-xs text-zinc-600">
              {tier === "guest" ? "Log in for Punchline + bigger lobbies. " : ""}
              <button onClick={() => navigate("/subscribe")} className="text-neon-pink hover:underline">Go Premium</button> for 12-player private rooms.
            </p>
          )}
        </div>

        {/* JOIN */}
        <div>
          <div className="flex items-center gap-2 mb-5">
            <Smartphone className="w-6 h-6 text-neon-lime" />
            <h2 className="font-display font-black text-2xl uppercase tracking-tight">Join a Game</h2>
          </div>
          <form onSubmit={join} className="rounded-2xl border-2 border-zinc-700 bg-zinc-950 p-6 space-y-4" data-testid="join-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Room Code</label>
              <input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 5))} data-testid="join-code"
                placeholder="ABCD" maxLength={5} autoCapitalize="characters"
                className="w-full mt-2 bg-black border-2 border-zinc-700 focus:border-neon-lime outline-none rounded-lg px-4 py-3 text-center font-display font-black text-3xl tracking-[0.4em] text-white uppercase" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Your Name</label>
              <input value={joinName} onChange={(e) => setJoinName(e.target.value.slice(0, 16))} data-testid="join-name"
                placeholder="Pick a nickname"
                className="w-full mt-2 bg-black border-2 border-zinc-700 focus:border-neon-lime outline-none rounded-lg px-4 py-3 text-white" />
            </div>
            <Button type="submit" disabled={joining || joinCode.length < 4 || !joinName.trim()} data-testid="join-submit"
              className="w-full bg-neon-lime text-black rounded-none font-bold uppercase tracking-widest h-12 hover:bg-white disabled:opacity-50">
              {joining ? <Loader2 className="w-5 h-5 animate-spin" /> : "Join Game"}
            </Button>
          </form>
          <p className="mt-4 text-sm text-zinc-500">No account needed to join — grab a friend's code and you're in.</p>
        </div>
      </div>

      <Dialog open={packModal} onOpenChange={setPackModal}>
        <DialogContent className="bg-zinc-950 border-2 border-neon-lime" data-testid="pack-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight flex items-center gap-2">
              <Crown className="w-5 h-5 text-neon-yellow" /> Create a Custom Pack
            </DialogTitle>
            <DialogDescription className="text-zinc-500">Premium hosts can add their own prompts (one per line). Great for inside jokes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input value={packName} onChange={(e) => setPackName(e.target.value.slice(0, 30))} data-testid="pack-name"
              placeholder="Pack name (e.g. Inside Jokes)"
              className="w-full bg-black border-2 border-zinc-700 focus:border-neon-lime outline-none rounded-lg px-4 py-2.5 text-white" />
            <Textarea value={packText} onChange={(e) => setPackText(e.target.value)} data-testid="pack-prompts"
              rows={7} placeholder="One prompt per line (at least 3)…&#10;The worst gift to give your boss&#10;A terrible name for a pet rock&#10;What the group chat is REALLY about" />
            <Button onClick={savePack} disabled={savingPack} data-testid="pack-save"
              className="w-full bg-neon-lime text-black rounded-none font-bold uppercase tracking-widest h-11 hover:bg-white">
              {savingPack ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Save Pack</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
